/**
 * Scheduled send — presets and the client-side mirror of the backend's window
 * for `scheduled_at` (at least 1 minute and at most 365 days ahead). Every time
 * here is in the viewer's local timezone; the request carries UTC ISO.
 */

export const MIN_SCHEDULE_LEAD_MS = 60 * 1000;
export const MAX_SCHEDULE_LEAD_MS = 365 * 24 * 60 * 60 * 1000;

export type ScheduleError = 'invalid' | 'tooSoon' | 'tooFar';

export function validateScheduledAt(when: Date | null | undefined, now: Date = new Date()): ScheduleError | null {
  if (!when || isNaN(when.getTime())) return 'invalid';
  const lead = when.getTime() - now.getTime();
  if (lead < MIN_SCHEDULE_LEAD_MS) return 'tooSoon';
  if (lead > MAX_SCHEDULE_LEAD_MS) return 'tooFar';
  return null;
}

export type SchedulePresetId = 'laterToday' | 'tomorrow' | 'monday';

export interface SchedulePreset {
  id: SchedulePresetId;
  at: Date;
}

function atLocal(base: Date, addDays: number, hour: number): Date {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + addDays, hour, 0, 0, 0);
  return d;
}

/**
 * Later today 5 PM / Tomorrow 9 AM / Monday 9 AM. A preset that is already in
 * the past (or inside the 1-minute minimum) is left out, and "Monday" is left
 * out when it is the same moment as "Tomorrow" (i.e. on a Sunday).
 */
export function schedulePresets(now: Date = new Date()): SchedulePreset[] {
  const out: SchedulePreset[] = [];
  const laterToday = atLocal(now, 0, 17);
  if (!validateScheduledAt(laterToday, now)) out.push({ id: 'laterToday', at: laterToday });
  const tomorrow = atLocal(now, 1, 9);
  out.push({ id: 'tomorrow', at: tomorrow });
  // Next Monday (never today): getDay() 0=Sun … 1=Mon.
  const daysToMonday = ((8 - now.getDay()) % 7) || 7;
  const monday = atLocal(now, daysToMonday, 9);
  if (monday.getTime() !== tomorrow.getTime()) out.push({ id: 'monday', at: monday });
  return out;
}

/** `YYYY-MM-DD` / `HH:mm` values for native date & time inputs, local time. */
export function toDateInputValue(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function toTimeInputValue(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Combine native date + time input values into a local Date (null if incomplete). */
export function fromDateTimeInputs(date: string, time: string): Date | null {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '');
  const tm = /^(\d{2}):(\d{2})/.exec(time || '');
  if (!dm || !tm) return null;
  const d = new Date(Number(dm[1]), Number(dm[2]) - 1, Number(dm[3]), Number(tm[1]), Number(tm[2]), 0, 0);
  return isNaN(d.getTime()) ? null : d;
}

/** Server timestamps may arrive without an offset (UTC wall-clock) — read them as UTC. */
export function parseServerDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso);
  const d = new Date(hasTz ? iso : `${iso}Z`);
  return isNaN(d.getTime()) ? null : d;
}

/** "Tue, Oct 21, 9:00 AM" in the viewer's locale and timezone. */
export function formatScheduleTime(d: Date, locale?: string): string {
  return d.toLocaleString(locale, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** "2:14 PM" in the viewer's locale and timezone. */
export function formatClockTime(d: Date, locale?: string): string {
  return d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
}
